import { memo, useEffect, useMemo } from 'react'

import { getFileUploadsCount } from '@tabeeb/modules/fileUploads/selectors'

import { Stack } from '@collaborate-health/uikit'
import { useDispatch, useSelector } from '@tabeeb/store'
import { TabPanel, TabPanelLocation } from '@tabeeb/enums'

import { SessionPanelState } from '@tabeeb/modules/sessionTabs/reducers'
import ExpandSessionTabButton from '@tabeeb/modules/sessionTabs/components/ExpandSessionTabButton'
import { getIsGalleryExpansionEnabled } from '@tabeeb/modules/appConfigState/selectors'
import { switchPanel } from '../../actions'
import { ExpandableSessionTabs, ReadonlyTabPanelLocationGroups, TabPanelLocationGroups } from '../../constants'
import { getSelectedPanel } from '../../selectors'

import PanelButton from '../TabButton'
import { ExpandButtonContainer, PanelButtonGroup } from './Navbar.styles'

export interface NavbarProps {
  location: TabPanelLocation
  readonly?: boolean
}

const Navbar: React.FC<NavbarProps> = ({ location, readonly = false }) => {
  const dispatch = useDispatch()
  const selectedTab = useSelector<SessionPanelState>((state) => getSelectedPanel(state, location))
  const isGalleryExpansionEnabled = useSelector(getIsGalleryExpansionEnabled)

  const fileUploadsCount = useSelector(getFileUploadsCount)

  const onTabButtonClick = (tab: TabPanel, tabLocation: TabPanelLocation) => {
    dispatch(switchPanel({ name: tab }, tabLocation))
  }

  useEffect(() => {
    if (selectedTab?.name === TabPanel.FileUploads && fileUploadsCount === 0) {
      dispatch(switchPanel({ name: TabPanel.Gallery }, TabPanelLocation.Right))
    }
  }, [selectedTab, fileUploadsCount, dispatch])

  const tabPanelLocationGroup = useMemo(
    () => (readonly ? ReadonlyTabPanelLocationGroups : TabPanelLocationGroups),
    [readonly]
  )

  const orientation = useMemo(() => (location === TabPanelLocation.Bottom ? 'horizontal' : 'vertical'), [location])
  const expandButtonOrientation = useMemo(() => (location === TabPanelLocation.Left ? 'right' : 'left'), [location])

  const displayExpandButton = useMemo(() => {
    if (!selectedTab) {
      return false
    }
    if (selectedTab.name === TabPanel.Gallery || selectedTab.name === TabPanel.FileUploads) {
      return isGalleryExpansionEnabled
    }

    return ExpandableSessionTabs.includes(selectedTab.name)
  }, [isGalleryExpansionEnabled, selectedTab])

  return (
    <Stack alignItems='flex-start' height={orientation === 'horizontal' ? 'auto' : '100%'}>
      <PanelButtonGroup
        data-location={location}
        value={selectedTab}
        orientation={orientation}
        exclusive
        onChange={(_, value: TabPanel) => {
          onTabButtonClick(value ?? selectedTab, location)
        }}
      >
        {tabPanelLocationGroup[location].map((tab) => (
          <PanelButton key={tab} value={tab} location={location} />
        ))}
      </PanelButtonGroup>
      {displayExpandButton && (
        <ExpandButtonContainer orientation={expandButtonOrientation}>
          <ExpandSessionTabButton panel={selectedTab.name} orientation={expandButtonOrientation} />
        </ExpandButtonContainer>
      )}
    </Stack>
  )
}

export default memo(Navbar)

